import { getCurrentPosition } from "../Mapbox/mapbox";
import { initializeModal, Modal } from "../Shared/Modal/modal";

type ClinicFinder = {
  close: () => void;
  isOpen: () => boolean;
  open: () => void;
};

const clinicFinders = new Map<string, ClinicFinder>();

const initializeClinicFinder = (rootFormElement: HTMLElement) => {
  const prefix = "clinic-finder-";

  const baseId = rootFormElement.getAttribute("id")?.substring(prefix.length) ?? "";

  const searchInputButton = rootFormElement.querySelector<HTMLInputElement>(
    "[data-search-input-button]"
  );

  const searchResultsContainer = rootFormElement.querySelector<HTMLElement>(
    "[data-search-results-container]"
  );

  const submitButtons = rootFormElement.querySelectorAll('[type="submit"]');

  const positionButton = rootFormElement.querySelector<HTMLElement>(
    "[data-position-button]"
  );

  const geolocationFailedModal = rootFormElement.querySelector<HTMLElement>(
    "[data-geolocation-failed-modal]"
  );

  if (!searchInputButton) {
    throw new Error(
      `Clinic finder with id ${rootFormElement.id} searchInputButton missing`
    );
  }

  if (!searchResultsContainer) {
    throw new Error(
      `Clinic finder with id ${rootFormElement.id} searchResultsContainer missing`
    );
  }

  let geolocationFailedModalDefinition: Modal | undefined;

  // Handle opening/closing search results container
  const isOpen = () => searchInputButton.getAttribute("aria-expanded") === "true";

  const close = () => {
    if (isOpen()) {
      searchInputButton.setAttribute("aria-expanded", "false");
      searchResultsContainer.setAttribute("inert", "true");
    }
  };

  const open = () => {
    if (!isOpen()) {
      searchInputButton.setAttribute("aria-expanded", "true");
      searchResultsContainer.removeAttribute("inert");
    }
  };

  document.addEventListener("click", event => {
    const isOutsideSearchResults =
      searchResultsContainer.contains(event.target as Node) === false;

    let isOutsideSubmitButtons = true;

    submitButtons.forEach(button => {
      if (button.contains(event.target as Node) === true) {
        isOutsideSubmitButtons = false;
      }
    });

    if (isOutsideSearchResults && isOutsideSubmitButtons) {
      close();
    }
  });

  document.addEventListener("keydown", event => {
    if (event.key === "Escape") {
      close();
    }
  });

  clinicFinders.set(baseId, {
    close,
    isOpen,
    open
  });

  if (positionButton) {
    // Use htmx:confirm together with htmx:configRequest to fetch google maps location data before sending
    // the request to the controller.
    let latitude: number | undefined;
    let longitude: number | undefined;

    htmx.on(positionButton, "htmx:confirm", e => {
      if (latitude === undefined || longitude === undefined) {
        const evt = e as ConfirmEvent;

        e.preventDefault();

        getCurrentPosition()
          .then(position => {
            latitude = position.latitude;
            longitude = position.longitude;

            evt.detail.issueRequest();
          })
          .catch(e => {
            console.warn(e);

            if (geolocationFailedModalDefinition) {
              geolocationFailedModalDefinition.open();
            }
          });
      }
    });

    htmx.on(positionButton, "htmx:configRequest", e => {
      const evt = e as ConfigRequestEvent;

      evt.detail.parameters.latitude = latitude;
      evt.detail.parameters.longitude = longitude;
    });
  }

  if (geolocationFailedModal) {
    geolocationFailedModalDefinition = initializeModal(geolocationFailedModal);
  }
};

htmx.onLoad(rootElement => {
  const id = rootElement.id;

  const prefix = "search-results-";

  // Check if the htmx call was for clinic search results, if so open the search results modal
  if (id.startsWith(prefix)) {
    const baseId = id.substring(prefix.length);

    const clinicFinder = clinicFinders.get(baseId);

    clinicFinder?.open();
  }
});

document
  .querySelectorAll<HTMLElement>("[data-clinic-finder]")
  .forEach(element => initializeClinicFinder(element));
